// WP-07 — AppRouter (LLD-05 §4): React Router v6 data router. Route table is
// derived from the screen registry so RBAC grants live in one place.
// WP-11: real screens land as route-level lazy chunks per vertical (LLD-05 §7)
// via SCREEN_COMPONENTS — registry ids map to their feature module; screens
// without an entry keep the WP-07 placeholder until their vertical ships.
// WP-10: the jobs vertical (O-03/O-04/O-05) joins the map.
import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { RequireAuth, RequireRole } from "./guards";
import { AppShell } from "./app-shell";
import { SignIn } from "../screens/sign-in";
import { NotFound, ScreenPlaceholder } from "../screens/screen-placeholder";
import { SCREENS, type ScreenDef } from "../screens/registry";
import { t } from "../i18n/i18n";
import { RouteTitle } from "./document-title";

// WP-11 — O-12 Clients (route-level chunks per LLD-05 §7).
const ClientsScreen = lazy(() => import("../features/clients/clients-screen"));
const ClientDetailScreen = lazy(() => import("../features/clients/client-detail-screen"));

// WP-14 — O-06/O-07/O-08 Scheduling & calendars.
const CalendarScreen = lazy(() => import("../features/scheduling/calendar-screen"));
const ScheduleScreen = lazy(() => import("../features/scheduling/schedule-screen"));
const ConfirmationScreen = lazy(() => import("../features/scheduling/confirmation-screen"));

// WP-15 — O-11 Quote lifecycle (list + detail).
const QuotesScreen = lazy(() => import("../features/quotes/quotes-screen"));
const QuoteDetailScreen = lazy(() => import("../features/quotes/quote-detail-screen"));

// WP-16 — O-09 Ready-for-Invoicing worklist (O-10 link-back is a modal within it).
const WorklistScreen = lazy(() => import("../features/invoicing/worklist-screen"));

// WP-17 — O-02 dashboard, O-13 foreman history (list + detail), O-15 org settings.
const DashboardScreen = lazy(() => import("../features/dashboard/dashboard-screen"));
const ForemenScreen = lazy(() => import("../features/foremen/foremen-screen"));
const ForemanHistoryScreen = lazy(() => import("../features/foremen/foreman-history-screen"));
const OrgSettingsScreen = lazy(() => import("../features/org-settings/org-settings-screen"));

// WP-32 — US 6.2-AC1 drill-down, US 4.1-AC4 client time summary, F-9 operator usage.
const DrilldownScreen = lazy(() => import("../features/dashboard/drilldown-screen"));
const TimeSummaryScreen = lazy(() => import("../screens/jobs/time-summary"));
const PlatformUsageScreen = lazy(() => import("../features/platform/platform-usage-screen"));

// WP-27 — O-14 catalogue admin (US 4.4).
const CatalogueScreen = lazy(() => import("../features/catalogue/catalogue-screen"));

// WP-10 — O-03/O-04/O-05 Jobs vertical (named exports mapped for lazy()).
const JobListScreen = lazy(() =>
  import("../screens/jobs/job-list").then((m) => ({ default: m.JobList })),
);
const NewJobScreen = lazy(() =>
  import("../screens/jobs/new-job").then((m) => ({ default: m.NewJob })),
);
const JobCardScreen = lazy(() =>
  import("../screens/jobs/job-card").then((m) => ({ default: m.JobCard })),
);

const SCREEN_COMPONENTS: Partial<Record<string, ComponentType>> = {
  "O-02": DashboardScreen,
  "O-13": ForemenScreen,
  "O-15": OrgSettingsScreen,
  "O-03": JobListScreen,
  "O-04": NewJobScreen,
  "O-05": JobCardScreen,
  "O-06": CalendarScreen,
  "O-07": ScheduleScreen,
  "O-08": ConfirmationScreen,
  // O-10 (link-back) is a modal within O-09; the /invoicing/link-back route
  // renders the worklist too so it is never a placeholder.
  "O-09": WorklistScreen,
  "O-10": WorklistScreen,
  "O-11": QuotesScreen,
  "O-12": ClientsScreen,
  "O-14": CatalogueScreen,
};

function screenById(id: string): ScreenDef {
  const screen = SCREENS.find((s) => s.id === id);
  if (!screen) throw new Error(`unknown screen id ${id}`);
  return screen;
}

// UX-4a (UX2-A11Y-1): every registry route — and the detail routes that reuse
// a registry grant — is titled from the screen's own titleKey.
function guarded(screen: ScreenDef, children: ReactNode): ReactNode {
  return (
    <RequireRole allowed={screen.roles}>
      <RouteTitle title={t(screen.titleKey)}>
        <Suspense fallback={<p aria-live="polite">…</p>}>{children}</Suspense>
      </RouteTitle>
    </RequireRole>
  );
}

export function buildRoutes(): RouteObject[] {
  return [
    { path: "/sign-in", element: <SignIn /> },
    // WP-32 (F-9) — the PLATFORM realm: its own sign-in, never the tenant shell.
    {
      path: "/platform/usage",
      element: (
        <Suspense fallback={<p aria-live="polite">…</p>}>
          <PlatformUsageScreen />
        </Suspense>
      ),
    },
    {
      path: "/",
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        ...SCREENS.map((screen) => {
          const Component = SCREEN_COMPONENTS[screen.id];
          return {
            path: screen.path,
            element: guarded(
              screen,
              Component ? <Component /> : <ScreenPlaceholder screen={screen} />,
            ),
          };
        }),
        // Detail routes beyond the registry share their parent screen's grant.
        {
          path: "clients/:clientId",
          element: guarded(screenById("O-12"), <ClientDetailScreen />),
        },
        {
          path: "quotes/:quoteId",
          element: guarded(screenById("O-11"), <QuoteDetailScreen />),
        },
        // WP-32 — the figure behind every dashboard number reuses the O-02 grant.
        {
          path: "dashboard/:metric",
          element: guarded(screenById("O-02"), <DrilldownScreen />),
        },
        // WP-32 — the client time summary reuses the O-05 job-card grant.
        {
          path: "jobs/:jobId/time-summary",
          element: guarded(screenById("O-05"), <TimeSummaryScreen />),
        },
        // WP-17 — per-foreman history (O-13 detail) reuses the O-13 grant.
        {
          path: "foremen/:foremanId/history",
          element: guarded(screenById("O-13"), <ForemanHistoryScreen />),
        },
        {
          path: "*",
          element: (
            <RouteTitle title={t("notFound.title")}>
              <NotFound />
            </RouteTitle>
          ),
        },
      ],
    },
  ];
}
